import { useNavigate } from '@tanstack/react-router';
import { Spinner } from '@tempo-io/sui-spinner';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoopLogo } from 'src/common/components/icons/LoopLogo';
import { z } from 'zod';
import { ExportModeProvider } from '@/common/components/ui/swimlane/context';
import { PublishedReadOnlyItemPanel } from '@/features/item-panel/components/PublishedReadOnlyItemPanel';
import { useItemPanelNavigation } from '@/features/item-panel/hooks/useItemPanelParams';
import { Legend } from './Legend';
import { NotFoundMessage } from './NotFoundMessage';
import { PasswordPromptModal } from './PasswordPromptModal';
import { PublishedContextProvider } from './PublishedContext';
import { PublishedSwimlane } from './PublishedSwimlane';
import { PublishedTruncationAlert } from './PublishedTruncationAlert';
import { PublishedViewAuthProvider, usePublishedViewAuth } from './PublishedViewAuthContext';
import type { PublishedMetadata, PublishingPreferences } from './types';
import { usePublishedSwimlane } from './usePublishedSwimlane';

/**
 * Search parameter schema for published swimlane routes.
 *
 * Optional query parameters for published view customization:
 * - embed: '1' to enable embed mode
 * - export: '1' to enable export mode
 * - title: '0' to hide title
 * - branding: '0' to hide branding/logo
 * - date: '0' to hide date
 * - legend: '0' to hide legend
 * - vision: Custom vision statement text
 *
 * Note: Query parameters can be parsed as numbers by TanStack Router (e.g., ?embed=1)
 * so we use union types to accept both string and number inputs.
 */
export const publishedSwimlaneSearchSchema = z.object({
  embed: z.union([z.string(), z.number()]).optional(),
  export: z.union([z.string(), z.number()]).optional(),
  title: z.union([z.string(), z.number()]).optional(),
  branding: z.union([z.string(), z.number()]).optional(),
  date: z.union([z.string(), z.number()]).optional(),
  legend: z.union([z.string(), z.number()]).optional(),
  vision: z.string().optional(),
  itemId: z.coerce.number().optional(),
});

export type PublishedSwimlaneSearchParams = z.infer<typeof publishedSwimlaneSearchSchema>;

export interface PublishedSwimlanePageProps {
  linkId: string;
  searchParams: PublishedSwimlaneSearchParams;
}

function isQueryParamTruthy(value: string | number | undefined): boolean {
  return value === '1' || value === 1;
}

/**
 * Discriminated union representing the view mode for published swimlanes.
 * - default: Normal published view with standard settings
 * - export: Export mode for PNG screenshot capture (allows scrolling)
 * - embed: Embed mode for iframe embedding (query param overrides)
 */
type ViewMode =
  | { readonly kind: 'default' }
  | { readonly kind: 'export' }
  | { readonly kind: 'embed' };

/**
 * Parses the view mode from URL search parameters.
 * Export mode takes precedence over embed mode if both are specified.
 */
function parseViewMode(searchParams: PublishedSwimlaneSearchParams): ViewMode {
  const hasExport = isQueryParamTruthy(searchParams.export);
  const hasEmbed = isQueryParamTruthy(searchParams.embed);
  if (hasExport) return { kind: 'export' };
  if (hasEmbed) return { kind: 'embed' };
  return { kind: 'default' };
}

interface ViewModeBehavior {
  readonly allowsQueryOverrides: boolean;
  readonly overflowClass: 'overflow-hidden' | '';
}

const DEFAULT_PUBLISHING_PREFERENCES: PublishingPreferences = {
  showTitle: true,
  showVisionStatement: false,
  showPublishDate: true,
  showLegend: true,
  showTempoBranding: true,
};

/**
 * Returns the behavioral configuration for a given view mode.
 * - default: No query overrides, hidden overflow
 * - export: Query overrides allowed, no overflow hiding (for screenshot capture)
 * - embed: Query overrides allowed, hidden overflow
 */
function getViewModeBehavior(mode: ViewMode): ViewModeBehavior {
  switch (mode.kind) {
    case 'default':
      return { allowsQueryOverrides: false, overflowClass: 'overflow-hidden' };
    case 'export':
      return { allowsQueryOverrides: true, overflowClass: '' };
    case 'embed':
      return { allowsQueryOverrides: true, overflowClass: 'overflow-hidden' };
  }
}

function getBooleanQueryParam(value: string | number | undefined, defaultValue: boolean): boolean {
  if (value === undefined) return defaultValue;
  return isQueryParamTruthy(value);
}

function getEffectivePreferences(
  metadata: PublishedMetadata,
  searchParams: PublishedSwimlaneSearchParams,
  allowsQueryOverrides: boolean,
): PublishingPreferences {
  const preferences = metadata.preferences ?? DEFAULT_PUBLISHING_PREFERENCES;
  if (!allowsQueryOverrides) return preferences;

  return {
    ...preferences,
    showTitle: getBooleanQueryParam(searchParams.title, true),
    showTempoBranding: getBooleanQueryParam(searchParams.branding, true),
    showPublishDate: getBooleanQueryParam(searchParams.date, true),
    showLegend: getBooleanQueryParam(searchParams.legend, true),
    showVisionStatement: searchParams.vision !== undefined ? true : preferences.showVisionStatement,
  };
}

/**
 * PublishedSwimlanePage - Displays a read-only published swimlane view
 *
 * This is a completely separate component from ViewPage.
 * No flags, no dual responsibility - just displays published swimlanes.
 */
export function PublishedSwimlanePage(props: PublishedSwimlanePageProps) {
  return (
    <PublishedViewAuthProvider>
      <PublishedSwimlanePageContent {...props} />
    </PublishedViewAuthProvider>
  );
}

function PublishedSwimlanePageContent({ linkId, searchParams }: PublishedSwimlanePageProps) {
  const { t } = useTranslation();
  const { submit } = usePublishedViewAuth();
  const result = usePublishedSwimlane(linkId);
  const navigate = useNavigate();
  const { closeItemPanel } = useItemPanelNavigation();
  const selectedItemId = searchParams.itemId != null ? String(searchParams.itemId) : null;
  const [expanded, setExpanded] = useState(false);

  const handleItemClick = useCallback(
    (itemId: string) => {
      navigate({ to: '.', search: (prev) => ({ ...prev, itemId: Number(itemId) }) });
      setExpanded(true);
    },
    [navigate],
  );

  const handleClose = useCallback(() => {
    setExpanded(false);
    closeItemPanel();
  }, [closeItemPanel]);

  // Exhaustive switch ensures all states are handled
  switch (result.status) {
    case 'loading':
      return (
        <div className="flex w-full min-h-screen items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <h1
              data-testid="publish-view-loading"
              className="text-2xl font-bold text-text-neutral-primary"
            >
              {t(($) => $.views.published.loading)}
            </h1>
            <Spinner size={32} />
          </div>
        </div>
      );

    case 'error':
      return (
        <div
          className="flex items-center justify-center h-screen text-xl text-text-error"
          role="alert"
        >
          {t(($) => $.views.published.errorLoading, { message: result.error.message })}
        </div>
      );

    case 'password_required':
      return <PasswordPromptModal authStatus="password_required" onSubmit={submit} />;

    case 'password_invalid':
      return <PasswordPromptModal authStatus="password_invalid" onSubmit={submit} />;

    case 'not_found':
      return <NotFoundMessage />;

    case 'success':
      break; // Fall through to render content
  }

  const {
    metadata,
    legendItems,
    items,
    itemDetails,
    customFieldDefinitions,
    hiddenFieldIds,
    customerLookup,
  } = result.data;

  const viewMode = parseViewMode(searchParams);
  const { allowsQueryOverrides, overflowClass } = getViewModeBehavior(viewMode);
  const preferences = getEffectivePreferences(metadata, searchParams, allowsQueryOverrides);

  const visionStatement =
    allowsQueryOverrides && searchParams.vision !== undefined
      ? searchParams.vision
      : metadata.visionStatement;

  const selectedEnrichedItem = selectedItemId ? (items[selectedItemId] ?? null) : null;
  const selectedItemDetails = selectedItemId ? (itemDetails[selectedItemId] ?? null) : null;

  const handleToggle = () => {
    setExpanded((prev) => !prev);
  };

  const showHeader = preferences.showTitle || preferences.showVisionStatement;

  const rootContainerClass = 'h-screen w-screen bg-bg-surface-base flex flex-col';
  const swimlaneContainerClass = `flex-1 ${overflowClass}`.trim();

  const publishDate = new Date(metadata.publishedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  const content = (
    <div data-testid="published-view-root" className={rootContainerClass}>
      {viewMode.kind !== 'export' && (
        <PublishedTruncationAlert key={linkId} itemLimit={metadata.itemLimit} />
      )}

      {showHeader && (
        <div className="flex-none px-8 pt-6 pb-4 border-b border-border-neutral">
          {preferences.showTitle && (
            <h1 className="text-2xl font-bold text-text-neutral-primary mb-2">{metadata.title}</h1>
          )}
          {preferences.showVisionStatement && visionStatement && (
            <div className="max-h-32 overflow-y-auto">
              <p
                data-testid="publish-legend-vision-statement"
                className="text-sm text-text-neutral-secondary whitespace-pre-wrap"
              >
                {visionStatement}
              </p>
            </div>
          )}
        </div>
      )}

      <div data-testid="swimlane-scroll-container" className={swimlaneContainerClass}>
        <PublishedSwimlane data={result.data} onItemClick={handleItemClick} />
      </div>

      <PublishedReadOnlyItemPanel
        itemDetails={selectedItemDetails}
        enrichedItem={selectedEnrichedItem}
        allItemDetails={itemDetails}
        enrichedItems={items}
        customFieldDefinitions={customFieldDefinitions}
        hiddenFieldIds={hiddenFieldIds}
        customerLookup={customerLookup}
        expanded={expanded}
        onToggle={handleToggle}
        onClose={handleClose}
      />

      {(preferences.showPublishDate || preferences.showTempoBranding || preferences.showLegend) && (
        <div className="flex-none py-4 w-full border-t border-border-neutral">
          <div className="flex items-center justify-between px-8">
            <div className="text-sm text-text-neutral-secondary">
              {preferences.showPublishDate && <span>Published on {publishDate}</span>}
            </div>
            <div className="flex items-center gap-3">
              {preferences.showTempoBranding && (
                <div className="flex items-center gap-2">
                  <LoopLogo width={24} height={24} />
                  <span className="text-sm font-semibold text-text-neutral-primary">
                    {t(($) => $.views.export.tempoBrandingName)}
                  </span>
                </div>
              )}
              {preferences.showLegend && <Legend items={legendItems} />}
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <PublishedContextProvider
      urlToken={linkId}
      panelItemId={searchParams.itemId}
      staticLookups={result.data}
    >
      {viewMode.kind === 'export' ? (
        <ExportModeProvider mode="png">{content}</ExportModeProvider>
      ) : (
        content
      )}
    </PublishedContextProvider>
  );
}
